// Security utilities for frontend

// Enforce HTTPS in production
export const enforceHTTPS = () => {
    if (process.env.NODE_ENV === 'production' &&
        typeof window !== 'undefined' &&
        window.location.protocol === 'http:') {
        window.location.href = window.location.href.replace('http:', 'https:');
    }
};

// Validate API URLs
export const validateAPIUrl = (url: string): boolean => {
    try {
        const urlObj = new URL(url);
        return urlObj.protocol === 'https:' || urlObj.hostname === 'localhost';
    } catch {
        return false;
    }
};

// Sanitize user input
export const sanitizeInput = (input: string): string => {
    return input
        .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
        .replace(/javascript:/gi, '')
        .replace(/on\w+\s*=/gi, '')
        .replace(/eval\s*\(/gi, '')
        .replace(/document\./gi, '')
        .replace(/window\./gi, '');
};

// Check if running in secure context
export const isSecureContext = (): boolean => {
    return typeof window !== 'undefined' && window.isSecureContext;
};

// Validate authentication token format
export const validateToken = (token: string): boolean => {
    // Basic JWT format validation
    const jwtRegex = /^[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+\.[A-Za-z0-9-_]*$/;
    return jwtRegex.test(token);
};

// Security headers check
export const checkSecurityHeaders = async (): Promise<boolean> => {
    try {
        const response = await fetch('/api/health', { method: 'HEAD' });
        const headers = response.headers;

        const requiredHeaders = [
            'x-content-type-options',
            'x-frame-options',
            'x-xss-protection',
            'strict-transport-security'
        ];

        return requiredHeaders.every(header => headers.get(header) !== null);
    } catch {
        return false;
    }
};

// CSP violation reporting
export const reportCSPViolation = (violation: any) => {
    if (process.env.NODE_ENV === 'production') {
        // Send to your logging service
        console.error('CSP Violation:', violation);
    }
};

// Initialize security features
export const initializeSecurity = () => {
    // Enforce HTTPS
    enforceHTTPS();

    // Check secure context
    if (!isSecureContext()) {
        console.warn('Application is not running in a secure context');
    }

    // Set up CSP violation reporting
    if (typeof window !== 'undefined') {
        window.addEventListener('securitypolicyviolation', reportCSPViolation);
    }
};

// Secure API request wrapper
export const secureRequest = async (
    url: string,
    options: RequestInit = {}
): Promise<Response> => {
    // Validate URL
    if (!validateAPIUrl(url)) {
        throw new Error('Invalid API URL');
    }

    // Add security headers
    const secureOptions: RequestInit = {
        ...options,
        headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            ...options.headers,
        },
        credentials: 'include',
    };

    return fetch(url, secureOptions);
}; 